import React from "react";
import { X, FileText, Check, Download } from "lucide-react";
import api from "@/lib/api";
import ChapterBlocks from "@/components/ChapterBlocks";

export default function DocumentReader({ document, progress, readonly, onClose, onProgressUpdate }) {
  if (!document) return null;
  const isRead = !!progress?.documents?.[document.id]?.read;

  const markRead = async () => {
    const { data } = await api.post("/progress/document", { document_id: document.id });
    onProgressUpdate?.(data);
    onClose();
  };

  const fakeDownload = () => {
    const blob = new Blob([`${document.title}\n\n${document.body}`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = `${document.title.replace(/\s+/g, "_")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const categoryLabel = {
    key: "Key Document",
    financial: "Financial Document",
    legal: "Legal Document",
  }[document.category] || "Document";

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/70 backdrop-blur-sm overflow-y-auto" data-testid="document-reader-modal">
      <div className="min-h-screen flex items-start md:items-center justify-center p-4 md:p-12">
        <div className="bg-[var(--bloom-bg)] max-w-2xl w-full relative bloom-rise shadow-2xl">
          <div className="sticky top-0 z-10 flex items-center justify-between px-8 md:px-12 py-5 border-b border-[var(--bloom-border)] bg-[var(--bloom-bg)]">
            <div className="flex items-center gap-4 text-xs text-[var(--bloom-ink-muted)] tracking-[0.15em] uppercase">
              <span className="flex items-center gap-2"><FileText size={12} /> {categoryLabel}</span>
              {isRead && <span className="flex items-center gap-1 text-[var(--bloom-brand)]"><Check size={12} /> Read</span>}
              {readonly && <span className="text-[var(--bloom-accent)]">Read-Only · Sign in Stage 3</span>}
            </div>
            <button onClick={onClose} className="bloom-btn bloom-btn-ghost" data-testid="document-close-btn">
              <X size={16} /> Close
            </button>
          </div>

          <article className="px-8 md:px-12 py-10 md:py-14">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)] mb-4">✦ Bloom Club Library ✦</div>
            <h1 className="font-serif text-3xl md:text-4xl leading-tight mb-3" data-testid="document-reader-title">{document.title}</h1>
            <p className="font-serif italic text-base text-[var(--bloom-ink-soft)] mb-8">{document.description}</p>
            <div className="bloom-divider mb-8">{document.pages} pages · v1.0 · Draft</div>

            {document.blocks && document.blocks.length > 0 ? (
              <ChapterBlocks blocks={document.blocks} />
            ) : (
              <div className="bloom-prose whitespace-pre-line">{document.body}</div>
            )}

            <div className="bloom-divider my-10">✦ end of document ✦</div>

            <div className="flex flex-col sm:flex-row gap-3 justify-between items-center">
              <button onClick={fakeDownload} className="bloom-btn bloom-btn-outline" data-testid="document-download-btn">
                <Download size={14} /> Download PDF
              </button>
              {!readonly ? (
                <button onClick={markRead} className="bloom-btn bloom-btn-primary" data-testid="document-mark-read-btn">
                  {isRead ? "Re-confirm Read" : "Mark As Read"}
                </button>
              ) : (
                <button onClick={onClose} className="bloom-btn bloom-btn-primary" data-testid="document-acknowledge-btn">
                  Acknowledged
                </button>
              )}
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}
